import React, { useState, useRef, useEffect } from "react";
import "./Google.css";
import { IoMdClose } from "react-icons/io";

export default function PricingPlans() {
    const [activeTab, setActiveTab] = useState("monthly");
    const [selectedPlan, setSelectedPlan] = useState(null);
    const popupRef = useRef(null); // 🔹 Reference for popup box

    // ✅ Close popup when clicking outside
    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (popupRef.current && !popupRef.current.contains(e.target)) {
                setSelectedPlan(null);
            }
        };
        if (selectedPlan) {
            document.addEventListener("mousedown", handleOutsideClick);
        } else {
            document.removeEventListener("mousedown", handleOutsideClick);
        }
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [selectedPlan]);

    // Shared full details (same across durations)
    const fullDetails = {
        resources: [
            { label: "Dedicated Resources", value: "" }, // values updated per plan below
        ],
        setup: [
            "WhatsApp Group Creation - Team Collaboration ✔",
            "0 Day Report for MOM Growth Comparison ✔",
            "Google Analytics - Setup and Review ✔",
            "Google Search Console - Setup and Review ✔",
            "Google Penalty Check ✔",
            "Website Indexing Check ✔",
            "Business Brief Meeting ✔",
            "Sitemap Submission ✔"
        ],
        onpage_seo: [],
        offpage_seo: [],
        technical_seo: [],
        reporting: [],
        support: []
    };

    // Build full details per plan (from screenshots)
    const planDetails = {
        lite: {
            resources: [
                { label: "Dedicated Resources", value: "1" },
                { label: "Digital Marketing Executive", value: "1" },
                { label: "Asst. Digital Marketing Executive", value: "❌" },
                { label: "Shared Resources", value: "2" },
                { label: "Digital Marketing Manager", value: "1" },
                { label: "Project Manager", value: "❌" },
                { label: "Graphic Designer", value: "1" },
                { label: "Content Writer", value: "Client's side (or pay separately)" },
                { label: "Web Developer", value: "Client's side (or pay separately)" }
            ],

            initial_setup_and_analysis: [
                { label: "WhatsApp Group Creation - Team Collaboration", value: "✔" },
                { label: "0 Day Report for MOM Growth Comparison", value: "✔" },
                { label: "Analytics - Setup and Review", value: "✔" },
                { label: "Social Media - Setup and Review", value: "✔" },
                { label: "Google Ads - Setup and Review", value: "✔" },
                { label: "GMB - Setup and Review", value: "✔" },
                { label: "Website Indexing Check", value: "✔" },
                { label: "Business Brief Meeting", value: "✔" }
            ],

            seo_services: [
                { label: "Initial SEO Audit", value: "Basic" },
                { label: "On-Page SEO", value: "20 Pages" },
                { label: "Off-Page SEO", value: "Basic Backlinks" },
                { label: "Technical SEO", value: "Basic" },
                { label: "Keyword Research", value: "100 Keywords" },
                { label: "SEO Content Optimization", value: "20 Pages" }
            ],

            local_seo: [
                { label: "Listing", value: "Basic Listing" },
                { label: "GMB Setup and Optimization", value: "✔" },
                { label: "Google Map Integration on Website", value: "❌" },
                { label: "GMB Posts", value: "❌" },
                { label: "NAP Syndication & Local Citations", value: "5" },
                { label: "Local Classifieds", value: "3" }
            ],

            google_ads_management: [
                { label: "Google Ads Setup", value: "Basic" },
                { label: "Ad Campaign Management", value: "1 Campaign" },
                { label: "Monthly Budget Handling", value: "Up to ₹15,000" },
                { label: "Ad Performance Optimization", value: "Monthly Review" },
                { label: "Keyword Bidding Strategy", value: "Basic" }
            ],

            social_media_marketing: [
                { label: "Social Media Strategy", value: "Basic" },
                { label: "Account Setup", value: "2 Platforms" },
                { label: "Content Posts", value: "8 Posts/Month" },
                { label: "Engagement Tactics", value: "Basic Engagement" },
                { label: "Paid Social Campaigns", value: "❌" },
                { label: "Social Media Analytics", value: "❌" }
            ],

            content_marketing: [
                { label: "Blog Writing", value: "❌" },
                { label: "Content Creation (Total Words)", value: "3,000 Words" },
                { label: "Video Content Production", value: "❌" },
                { label: "Ebooks and Whitepapers", value: "❌" },
                { label: "Infographics", value: "❌" }
            ],

            email_marketing: [
                { label: "Email Campaign Setup", value: "❌" },
                { label: "Newsletter Design and Sending", value: "❌" },
                { label: "Email List Management", value: "❌" },
                { label: "Automated Email Responses", value: "❌" }
            ],

            planning_and_reporting: [
                { label: "Planning", value: "Basic" },
                { label: "Strategy", value: "Basic" },
                { label: "Digital Marketing Report", value: "Monthly" },
                { label: "Keyword Ranking Report", value: "Monthly" },
                { label: "Backlinks Report", value: "Monthly" },
                { label: "Daily Updates in Group", value: "❌" }
            ],

            support_and_consultation: [
                { label: "Phone Support", value: "✔" },
                { label: "Chat Support", value: "✔" },
                { label: "Email Support", value: "✔" },
                { label: "Monthly Consultation", value: "1 hour" },
                { label: "PRP (Performance Review & Planning)", value: "Monthly" }
            ]
        },

        standard: {
            resources: [
                { label: "Dedicated Resources", value: "2" },
                { label: "Digital Marketing Executive", value: "1" },
                { label: "Asst. Digital Marketing Executive", value: "1" },
                { label: "Shared Resources", value: "2" },
                { label: "Digital Marketing Manager", value: "1" },
                { label: "Project Manager", value: "❌" },
                { label: "Graphic Designer", value: "1" },
                { label: "Content Writer", value: "Client's side (or pay separately)" },
                { label: "Web Developer", value: "WordPress only (SEO Corrections)" }
            ],

            initial_setup_and_analysis: [
                { label: "WhatsApp Group Creation - Team Collaboration", value: "✔" },
                { label: "0 Day Report for MOM Growth Comparison", value: "✔" },
                { label: "Analytics - Setup and Review", value: "✔" },
                { label: "Social Media - Setup and Review", value: "✔" },
                { label: "Google Ads - Setup and Review", value: "✔" },
                { label: "GMB - Setup and Review", value: "✔" },
                { label: "Website Indexing Check", value: "✔" },
                { label: "Business Brief Meeting", value: "✔" }
            ],

            seo_services: [
                { label: "Initial SEO Audit", value: "Comprehensive" },
                { label: "On-Page SEO", value: "30 Pages" },
                { label: "Off-Page SEO", value: "Advanced Backlinks" },
                { label: "Technical SEO", value: "Moderate" },
                { label: "Keyword Research", value: "150 Keywords" },
                { label: "SEO Content Optimization", value: "30 Pages" }
            ],

            local_seo: [
                { label: "Listing", value: "Enhanced Listings" },
                { label: "GMB Setup and Optimization", value: "✔" },
                { label: "Google Map Integration on Website", value: "✔" },
                { label: "GMB Posts", value: "4" },
                { label: "NAP Syndication & Local Citations", value: "10" },
                { label: "Local Classifieds", value: "5" }
            ],

            google_ads_management: [
                { label: "Google Ads Setup", value: "Standard" },
                { label: "Ad Campaign Management", value: "3 Campaigns" },
                { label: "Monthly Budget Handling", value: "Up to ₹45,000" },
                { label: "Ad Performance Optimization", value: "Bi-weekly Review" },
                { label: "Keyword Bidding Strategy", value: "Enhanced" }
            ],

            social_media_marketing: [
                { label: "Social Media Strategy", value: "Standard" },
                { label: "Account Setup", value: "3 Platforms" },
                { label: "Content Posts", value: "16 Posts/Month" },
                { label: "Engagement Tactics", value: "Enhanced Engagement" },
                { label: "Paid Social Campaigns", value: "Up to ₹5,000/Month" },
                { label: "Social Media Analytics", value: "❌" }
            ],

            content_marketing: [
                { label: "Blog Writing", value: "4 Posts/Month" },
                { label: "Content Creation (Total Words)", value: "6,000 Words" },
                { label: "Video Content Production", value: "❌" },
                { label: "Ebooks and Whitepapers", value: "❌" },
                { label: "Infographics", value: "❌" }
            ],

            email_marketing: [
                { label: "Email Campaign Setup", value: "1 Campaign/Month" },
                { label: "Newsletter Design and Sending", value: "Monthly" },
                { label: "Email List Management", value: "Up to 500 Contacts" },
                { label: "Automated Email Responses", value: "❌" }
            ],

            planning_and_reporting: [
                { label: "Planning", value: "Intermediate" },
                { label: "Strategy", value: "Intermediate" },
                { label: "Digital Marketing Report", value: "Monthly" },
                { label: "Keyword Ranking Report", value: "Monthly" },
                { label: "Backlinks Report", value: "Monthly" },
                { label: "Daily Updates in Group", value: "❌" }
            ],

            support_and_consultation: [
                { label: "Phone Support", value: "✔" },
                { label: "Chat Support", value: "✔" },
                { label: "Email Support", value: "✔" },
                { label: "Monthly Consultation", value: "2 hours" },
                { label: "PRP (Performance Review & Planning)", value: "Monthly" }
            ]
        },

        advance: {
            resources: [
                { label: "Dedicated Resources", value: "2" },
                { label: "Digital Marketing Executive", value: "1" },
                { label: "Asst. Digital Marketing Executive", value: "1" },
                { label: "Shared Resources", value: "3" },
                { label: "Digital Marketing Manager", value: "1" },
                { label: "Project Manager", value: "1" },
                { label: "Graphic Designer", value: "1" },
                { label: "Content Writer", value: "Client's side (or pay separately)" },
                { label: "Web Developer", value: "WordPress only (SEO Corrections)" }
            ],

            initial_setup_and_analysis: [
                { label: "WhatsApp Group Creation - Team Collaboration", value: "✔" },
                { label: "0 Day Report for MOM Growth Comparison", value: "✔" },
                { label: "Analytics - Setup and Review", value: "✔" },
                { label: "Social Media - Setup and Review", value: "✔" },
                { label: "Google Ads - Setup and Review", value: "✔" },
                { label: "GMB - Setup and Review", value: "✔" },
                { label: "Website Indexing Check", value: "✔" },
                { label: "Business Brief Meeting", value: "✔" }
            ],

            seo_services: [
                { label: "Initial SEO Audit", value: "In-depth" },
                { label: "On-Page SEO", value: "50 Pages" },
                { label: "Off-Page SEO", value: "Premium Backlinks" },
                { label: "Technical SEO", value: "Advanced" },
                { label: "Keyword Research", value: "250 Keywords" },
                { label: "SEO Content Optimization", value: "50 Pages" }
            ],

            local_seo: [
                { label: "Listing", value: "Comprehensive Local SEO" },
                { label: "GMB Setup and Optimization", value: "✔" },
                { label: "Google Map Integration on Website", value: "✔" },
                { label: "GMB Posts", value: "8" },
                { label: "NAP Syndication & Local Citations", value: "15" },
                { label: "Local Classifieds", value: "10" }
            ],

            google_ads_management: [
                { label: "Google Ads Setup", value: "Advanced" },
                { label: "Ad Campaign Management", value: "5 Campaigns" },
                { label: "Monthly Budget Handling", value: "Up to ₹75,000" },
                { label: "Ad Performance Optimization", value: "Weekly Optimization" },
                { label: "Keyword Bidding Strategy", value: "Advanced" }
            ],

            social_media_marketing: [
                { label: "Social Media Strategy", value: "Advanced" },
                { label: "Account Setup", value: "4 Platforms" },
                { label: "Content Posts", value: "Daily Posts" },
                { label: "Engagement Tactics", value: "Strategic Engagement" },
                { label: "Paid Social Campaigns", value: "Up to ₹10,000/Month" },
                { label: "Social Media Analytics", value: "Basic Reports" }
            ],

            content_marketing: [
                { label: "Blog Writing", value: "8 Posts/Month" },
                { label: "Content Creation (Total Words)", value: "10,000 Words" },
                { label: "Video Content Production", value: "2 Videos/Month" },
                { label: "Ebooks and Whitepapers", value: "❌" },
                { label: "Infographics", value: "❌" }
            ],

            email_marketing: [
                { label: "Email Campaign Setup", value: "2 Campaigns/Month" },
                { label: "Newsletter Design and Sending", value: "Bi-weekly" },
                { label: "Email List Management", value: "Up to 1,000 Contacts" },
                { label: "Automated Email Responses", value: "Basic Automation" }
            ],

            planning_and_reporting: [
                { label: "Planning", value: "Advanced" },
                { label: "Strategy", value: "Advanced" },
                { label: "Digital Marketing Report", value: "Monthly" },
                { label: "Keyword Ranking Report", value: "Monthly" },
                { label: "Backlinks Report", value: "Bi-Weekly" },
                { label: "Daily Updates in Group", value: "✔" }
            ],

            support_and_consultation: [
                { label: "Phone Support", value: "✔" },
                { label: "Chat Support", value: "✔" },
                { label: "Email Support", value: "✔" },
                { label: "Monthly Consultation", value: "4 hours" },
                { label: "PRP (Performance Review & Planning)", value: "Monthly" }
            ]
        },

        enterprise: {
            resources: [
                { label: "Dedicated Resources", value: "2" },
                { label: "Digital Marketing Executive", value: "1" },
                { label: "Asst. Digital Marketing Executive", value: "1" },
                { label: "Shared Resources", value: "5" },
                { label: "Digital Marketing Manager", value: "1" },
                { label: "Project Manager", value: "1" },
                { label: "Graphic Designer", value: "1" },
                { label: "Content Writer", value: "1" },
                { label: "Web Developer", value: "1" }
            ],

            initial_setup_and_analysis: [
                { label: "WhatsApp Group Creation - Team Collaboration", value: "✔" },
                { label: "0 Day Report for MOM Growth Comparison", value: "✔" },
                { label: "Analytics - Setup and Review", value: "✔" },
                { label: "Social Media - Setup and Review", value: "✔" },
                { label: "Google Ads - Setup and Review", value: "✔" },
                { label: "GMB - Setup and Review", value: "✔" },
                { label: "Website Indexing Check", value: "✔" },
                { label: "Business Brief Meeting", value: "✔" }
            ],

            seo_services: [
                { label: "Initial SEO Audit", value: "In-depth with Competitor Analysis" },
                { label: "On-Page SEO", value: "100+ Pages" },
                { label: "Off-Page SEO", value: "Custom Link Building Strategy" },
                { label: "Technical SEO", value: "Comprehensive" },
                { label: "Keyword Research", value: "500 Keywords" },
                { label: "SEO Content Optimization", value: "100+ Pages" }
            ],

            local_seo: [
                { label: "Listing", value: "Custom Local SEO Strategy" },
                { label: "GMB Setup and Optimization", value: "✔" },
                { label: "Google Map Integration on Website", value: "✔" },
                { label: "GMB Posts", value: "16" },
                { label: "NAP Syndication & Local Citations", value: "20" },
                { label: "Local Classifieds", value: "15" }
            ],

            google_ads_management: [
                { label: "Google Ads Setup", value: "Customized Setup" },
                { label: "Ad Campaign Management", value: "10+ Campaigns" },
                { label: "Monthly Budget Handling", value: "₹150,000+" },
                { label: "Ad Performance Optimization", value: "Continuous Optimization" },
                { label: "Keyword Bidding Strategy", value: "Strategic" }
            ],

            social_media_marketing: [
                { label: "Social Media Strategy", value: "Customized Strategy" },
                { label: "Account Setup", value: "5 Platforms" },
                { label: "Content Posts", value: "Twice Daily Posts" },
                { label: "Engagement Tactics", value: "Full Engagement Strategy" },
                { label: "Paid Social Campaigns", value: "Custom Budget" },
                { label: "Social Media Analytics", value: "Advanced Reports" }
            ],

            content_marketing: [
                { label: "Blog Writing", value: "16+ Posts/Month" },
                { label: "Content Creation (Total Words)", value: "20,000+ Words" },
                { label: "Video Content Production", value: "4 Videos/Month" },
                { label: "Ebooks and Whitepapers", value: "Monthly - 1" },
                { label: "Infographics", value: "Monthly - 1" }
            ],

            email_marketing: [
                { label: "Email Campaign Setup", value: "Weekly Campaigns" },
                { label: "Newsletter Design and Sending", value: "Weekly" },
                { label: "Email List Management", value: "Up to 5,000 Contacts" },
                { label: "Automated Email Responses", value: "Advanced Automation" }
            ],

            planning_and_reporting: [
                { label: "Planning", value: "Comprehensive" },
                { label: "Strategy", value: "Comprehensive" },
                { label: "Digital Marketing Report", value: "Bi-Weekly" },
                { label: "Keyword Ranking Report", value: "Bi-Weekly" },
                { label: "Backlinks Report", value: "Bi-Weekly" },
                { label: "Daily Updates in Group", value: "✔" }
            ],

            support_and_consultation: [
                { label: "Phone Support", value: "✔" },
                { label: "Chat Support", value: "✔" },
                { label: "Email Support", value: "✔" },
                { label: "Monthly Consultation", value: "8 hours" },
                { label: "PRP (Performance Review & Planning)", value: "Monthly" }
            ]
        }

    };

    // Price table for each duration (from your screenshots / confirmed)
    const priceTable = {
        monthly: {
            lite: "₹75,000",
            standard: "₹100,000",
            advance: "₹200,000",
            enterprise: "₹500,000"
        },
        quarterly: {
            lite: "₹67,500",
            standard: "₹90,000",
            advance: "₹180,000",
            enterprise: "₹450,000"
        },
        halfyear: {
            lite: "₹60,000",
            standard: "₹80,000",
            advance: "₹160,000",
            enterprise: "₹400,000"
        },
        yearly: {
            lite: "₹52,500",
            standard: "₹70,000",
            advance: "₹140,000",
            enterprise: "₹350,000"
        }
    };

    // Build plans array from priceTable for the active tab
    const planMeta = [
        { id: "lite", title: "LITE", type: "Small Business" },
        { id: "standard", title: "STANDARD", type: "Moderate Competition" },
        { id: "advance", title: "ADVANCE", type: "Ecommerce / High Competition" },
        { id: "enterprise", title: "ENTERPRISE", type: "Brands / High Competition" }
    ];

    const plans = planMeta.map(p => ({
        id: p.id,
        title: p.title,
        price: priceTable[activeTab][p.id],
        type: p.type,
        features: (() => {
            if (p.id === "lite") return [
                "Dedicated Digital Marketing Executive and Shared Digital Marketin...",
                "Initial Setup and Analysis offered",
                "Basic SEO Services",
                "Basic Strategy Planning and Monthly Reporting",
                "1 hour Monthly Consultation and PRP"
            ];
            if (p.id === "standard") return [
                "Dedicated Digital Marketing Executive and Assistant",
                "Initial Setup and Analysis offered",
                "Comprehensive SEO Services",
                "Intermediate Strategy Planning and Monthly Reporting",
                "2 hour Monthly Consultation and PRP"
            ];
            if (p.id === "advance") return [
                "Dedicated Executive support, Shared Project Manager, Graphic, Con...",
                "Initial Setup and Analysis offered",
                "In-depth SEO Services",
                "Advanced Strategy Planning and Bi-weekly Reporting",
                "4 hour Monthly Consultation and PRP"
            ];
            return [
                "Dedicated Executive support, Shared Project Manager, Graphic, Con...",
                "Initial Setup and Analysis offered",
                "In-depth SEO Services with Competitor Analysis",
                "Comprehensive Strategy Planning and Bi-weekly Reporting",
                "8 hour Monthly Consultation and PRP"
            ];
        })(),
        details: planDetails[p.id]
    }));

    return (
        <div className="pricing-wrapper">
            <section className="price-header" />
            <section className="services-header">
                <div className="photo-text">Digital Marketing
                    <p>Packages & Plans</p>
                </div>
            </section>

            <div className="tabs">
                {[
                    { id: "monthly", label: <>Month <br /> (1 Month)</> },
                    { id: "quarterly", label: <>Quarterly <br /> (3 Months)</> },
                    { id: "halfyear", label: <>Half Yearly <br /> (6 Months)</> },
                    { id: "yearly", label: <>Yearly <br /> (12 Months)</> },
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`tab-btn ${activeTab === tab.id ? "active" : ""}`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="plans-grid">
                {plans.map(plan => (
                    <div key={plan.id} className="plan-card">
                        {/* POPULAR TAG */}
                        {plan.id === "standard" && <div className="popular-tag">Popular</div>}

                        {/* DISCOUNT + PRICE */}
                        <h3 className="plan-title">{plan.title}</h3>

                        <div className="price-section">
                            {/* Show original price & discount only if NOT monthly */}
                            {activeTab !== "monthly" && (
                                <>
                                    <p className="original-price">
                                        ₹{(() => {
                                            const monthlyPrices = {
                                                lite: 75000,
                                                standard: 100000,
                                                advance: 200000,
                                                enterprise: 500000
                                            };

                                            // Multiplying months so original price = base × months
                                            const multipliers = {
                                                quarterly: 1,
                                                halfyear: 1,
                                                yearly: 1
                                            };

                                            return (monthlyPrices[plan.id] * multipliers[activeTab]).toLocaleString();
                                        })()}
                                    </p>

                                    {/* Dynamic discount badge */}
                                    <span className="discount-badge">
                                        {activeTab === "quarterly" && "10% Off"}
                                        {activeTab === "halfyear" && "20% Off"}
                                        {activeTab === "yearly" && "30% Off"}
                                    </span>
                                </>
                            )}
                        </div>



                        <h2 className="plan-price">{plan.price}</h2>
                        <p className="per-month">/Month</p>
                        <button className="quote-btn">Get Quote</button>

                        <hr className="divider" />
                        <p className="plan-type">{plan.type}</p>

                        <ul className="feature-list">
                            {plan.features.map((f, idx) => (
                                <li key={idx}>{f}</li>
                            ))}
                        </ul>

                        <button className="details-btn" onClick={() => setSelectedPlan(plan)}>
                            <span className="details-btn-txt">View Details</span>
                        </button>
                    </div>

                ))}
            </div>

            {selectedPlan && (
                <div className="popup-overlay">
                    <div className="popup-box">
                        <button className="close-btn" onClick={() => setSelectedPlan(null)}><IoMdClose /></button>
                        <h2 className="popup-title">{selectedPlan.title}</h2>

                        <div className="popup-grid">

                            {/* Resources */}
                            <div className="card">
                                <h3 className="popup-subtitle">Resources</h3>
                                <ul>
                                    {selectedPlan.details.resources?.map((item, i) => (
                                        <li key={i}>
                                            <span className="left">{item.label}</span>
                                            <span className="sep">:</span>
                                            <span className="right">{item.value}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Initial Setup & Analysis */}
                            <div className="card">
                                <h3 className="popup-subtitle">Initial Setup & Analysis</h3>
                                <ul>
                                    {selectedPlan.details.initial_setup_and_analysis?.map((item, i) => (
                                        <li key={i}>
                                            <span className="left">{item.label}</span>
                                            <span className="sep">:</span>
                                            <span className="right">{item.value}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* SEO Services */}
                            <div className="card">
                                <h3 className="popup-subtitle">SEO Services</h3>
                                <ul>
                                    {selectedPlan.details.seo_services?.map((item, i) => (
                                        <li key={i}>
                                            <span className="left">{item.label}</span>
                                            <span className="sep">:</span>
                                            <span className="right">{item.value}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Local SEO */}
                            <div className="card">
                                <h3 className="popup-subtitle">Local SEO</h3>
                                <ul>
                                    {selectedPlan.details.local_seo?.map((item, i) => (
                                        <li key={i}>
                                            <span className="left">{item.label}</span>
                                            <span className="sep">:</span>
                                            <span className="right">{item.value}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Google Ads Management */}
                            <div className="card">
                                <h3 className="popup-subtitle">Google Ads Management</h3>
                                <ul>
                                    {selectedPlan.details.google_ads_management?.map((item, i) => (
                                        <li key={i}>
                                            <span className="left">{item.label}</span>
                                            <span className="sep">:</span>
                                            <span className="right">{item.value}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Social Media Marketing */}
                            <div className="card">
                                <h3 className="popup-subtitle">Social Media Marketing</h3>
                                <ul>
                                    {selectedPlan.details.social_media_marketing?.map((item, i) => (
                                        <li key={i}>
                                            <span className="left">{item.label}</span>
                                            <span className="sep">:</span>
                                            <span className="right">{item.value}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Content Marketing */}
                            <div className="card">
                                <h3 className="popup-subtitle">Content Marketing</h3>
                                <ul>
                                    {selectedPlan.details.content_marketing?.map((item, i) => (
                                        <li key={i}>
                                            <span className="left">{item.label}</span>
                                            <span className="sep">:</span>
                                            <span className="right">{item.value}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Email Marketing */}
                            <div className="card">
                                <h3 className="popup-subtitle">Email Marketing</h3>
                                <ul>
                                    {selectedPlan.details.email_marketing?.map((item, i) => (
                                        <li key={i}>
                                            <span className="left">{item.label}</span>
                                            <span className="sep">:</span>
                                            <span className="right">{item.value}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Planning & Reporting */}
                            <div className="card">
                                <h3 className="popup-subtitle">Planning & Reporting</h3>
                                <ul>
                                    {selectedPlan.details.planning_and_reporting?.map((item, i) => (
                                        <li key={i}>
                                            <span className="left">{item.label}</span>
                                            <span className="sep">:</span>
                                            <span className="right">{item.value}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Support & Consultation */}
                            <div className="card">
                                <h3 className="popup-subtitle">Support & Consultation</h3>
                                <ul>
                                    {selectedPlan.details.support_and_consultation?.map((item, i) => (
                                        <li key={i}>
                                            <span className="left">{item.label}</span>
                                            <span className="sep">:</span>
                                            <span className="right">{item.value}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                        </div>



                        <a
                            href={`https://wa.me/919624883636?text=${encodeURIComponent(
                                `Hi! I'm interested in the ${selectedPlan.title} plan (${activeTab.toUpperCase()}). Please share more details.`
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="subscribe-btn"
                        >
                            Subscribe Now
                        </a>

                    </div>
                </div>
            )}
        </div>
    );
}
